import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInventory } from "../../context/InventoryContext";
import Price from "../../components/common/Price";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import NavigationBlocker from "../../components/common/NavigationBlocker";

// Icons
import { 
  CubeIcon, 
  CurrencyRupeeIcon, 
  MapPinIcon, 
  ClipboardDocumentListIcon 
} from "@heroicons/react/24/outline";

const InventoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { saveInventory, getInventory, warehouses, addStock } = useInventory();
  const isEditing = Boolean(id);

  const [isDirty, setIsDirty] = useState(false);

  // === 1. FORM STATE ===
  const [formData, setFormData] = useState({
    itemName: "",
    sku: "",
    category: "",
    unit: "Units",
    unitPrice: "",
    reorderLevel: "10",
    reorderQuantity: "", 
    description: "",
    status: "in_stock"
  });

  // === 2. INITIAL STOCK STATE (New Items Only) ===
  const [initialStock, setInitialStock] = useState({
    quantity: "",
    warehouseId: "",
    binName: ""
  });

  // Calculate Total Value dynamically for display
  const totalValue = (Number(formData.unitPrice || 0) * Number(initialStock.quantity || 0)).toFixed(2);

  // === 3. LOAD DATA ===
  useEffect(() => {
    if (isEditing) {
      const data = getInventory(id);
      if (data) {
        setFormData({
          itemName: data.itemName || data.item_name || "",
          sku: data.sku || "",
          category: data.category || "",
          unit: data.unit || "Units",
          unitPrice: data.unitPrice || data.unit_price || "",
          reorderLevel: data.reorderLevel || data.reorder_level || "10",
          reorderQuantity: data.reorderQuantity || data.reorder_quantity || "",
          description: data.description || "",
          status: data.status || "in_stock"
        });
      }
    }
  }, [id, getInventory, isEditing]);

  // === 4. HANDLERS ===
  const handleChange = (e) => {
    setIsDirty(true);
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStockChange = (e) => {
    setIsDirty(true);
    const { name, value } = e.target;
    setInitialStock((prev) => ({ ...prev, [name]: value }));
  };

  // Auto-select first Bin when Warehouse changes
  useEffect(() => {
    if (initialStock.warehouseId) {
      const wh = warehouses.find(w => String(w.id) === String(initialStock.warehouseId));
      if (wh && wh.bins && wh.bins.length > 0) {
        setInitialStock(prev => ({ ...prev, binName: wh.bins[0] }));
      } else {
        setInitialStock(prev => ({ ...prev, binName: "" }));
      }
    }
  }, [initialStock.warehouseId, warehouses]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.itemName) return toast.error("Item Name is required");
    if (!formData.unitPrice) return toast.error("Unit Price is required");

    try {
        // Prepare Payload
        const payload = {
            ...formData,
            id: isEditing ? id : null,
            unitPrice: Number(formData.unitPrice),
            reorderLevel: Number(formData.reorderLevel),
            reorderQuantity: Number(formData.reorderQuantity)
        };

        // Save to DB
        const response = await saveInventory(payload); 
        
        // Handle Opening Stock (Only for new items)
        if (!isEditing && initialStock.quantity > 0 && response?.id) {
            if (!initialStock.warehouseId || !initialStock.binName) {
                toast.warning("Product saved, but Stock not added (No Location selected).");
            } else {
                await addStock(
                    response.id, 
                    Number(initialStock.quantity), 
                    initialStock.warehouseId, 
                    initialStock.binName, 
                    "Initial Stock Entry", 
                    "Opening Balance"
                );
            }
        }

        toast.success(isEditing ? "Inventory Updated!" : "Item Created Successfully!");
        setIsDirty(false);
        setTimeout(() => navigate("/admin/inventory"), 1000);

    } catch (error) {
        console.error(error);
        toast.error("Operation Failed");
    }
  };

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen flex justify-center">
        <div className="w-full max-w-4xl bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          
          {/* Header */}
          <div className="bg-gray-900 px-8 py-6 text-white flex justify-between items-center">
              <div>
                  <h1 className="text-base font-bold">{isEditing ? "Edit Product" : "Add New Inventory Item"}</h1>
                  <p className="text-gray-400 text-sm mt-1">Manage master data and logistics.</p>
              </div>
              <CubeIcon className="w-10 h-10 text-gray-500" />
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-8">
              
              {/* SECTION 1: MASTER DATA */}
              <div>
                  <h3 className="text-base font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <ClipboardDocumentListIcon className="w-5 h-5" /> Product Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="col-span-2 md:col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Item Name <span className="text-red-500">*</span></label>
                          <input type="text" name="itemName" value={formData.itemName} onChange={handleChange} className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="e.g. 43-Grade Cement" required />
                      </div>
                      
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">SKU / Code</label>
                          <input type="text" name="sku" value={formData.sku} onChange={handleChange} className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="e.g. MAT-CEM-001" />
                      </div>

                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <select name="category" value={formData.category} onChange={handleChange} className="w-full border border-gray-300 px-4 py-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                              <option value="">-- Select Category --</option>
                              <option value="Cement">Cement</option>
                              <option value="Steel">Steel</option>
                              <option value="Aggregates">Aggregates</option>
                              <option value="Bricks">Bricks</option>
                              <option value="Plumbing">Plumbing</option>
                              <option value="Electrical">Electrical</option>
                              <option value="Finishing">Finishing</option>
                              <option value="General">General</option>
                          </select>
                      </div>

                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
                          <select name="unit" value={formData.unit} onChange={handleChange} className="w-full border border-gray-300 px-4 py-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                              <option value="Units">Units (Nos)</option>
                              <option value="Kg">Kilograms (Kg)</option>
                              <option value="Ton">Tons</option>
                              <option value="Ltr">Liters</option>
                              <option value="Bags">Bags</option>
                              <option value="Sqft">Sq. Ft</option>
                              <option value="Cum">Cubic Meter (Cum)</option>
                          </select>
                      </div>
                  </div>
              </div>

              {/* SECTION 2: COSTING & LOGISTICS */}
              <div>
                  <h3 className="text-base font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <CurrencyRupeeIcon className="w-5 h-5" /> Costing & Logistics
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price (₹) <span className="text-red-500">*</span></label>
                          <input type="number" name="unitPrice" value={formData.unitPrice} onChange={handleChange} className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="0.00" min="0" required />
                      </div>

                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Low Stock Alert Level</label>
                          <input type="number" name="reorderLevel" value={formData.reorderLevel} onChange={handleChange} className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="10" min="0" />
                      </div>

                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Quantity</label>
                          <input type="number" name="reorderQuantity" value={formData.reorderQuantity} onChange={handleChange} className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="e.g. 50" min="0" />
                      </div>

                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <input type="text" name="description" value={formData.description} onChange={handleChange} className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition" placeholder="Specs, Brand, etc." />
                      </div>
                  </div>
              </div>

              {/* SECTION 3: OPENING STOCK (Only for New Items) */}
              {!isEditing && (
                  <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                      <h3 className="text-base font-bold text-blue-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                          <MapPinIcon className="w-5 h-5" /> Opening Stock (Optional)
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Quantity</label>
                              <input 
                                  type="number" 
                                  name="quantity" 
                                  value={initialStock.quantity} 
                                  onChange={handleStockChange} 
                                  className="w-full border border-blue-200 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" 
                                  placeholder="0" 
                                  min="0" 
                              />
                          </div>

                          <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Warehouse</label>
                              <select 
                                  name="warehouseId" 
                                  value={initialStock.warehouseId} 
                                  onChange={handleStockChange} 
                                  className="w-full border border-blue-200 px-4 py-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                  disabled={!initialStock.quantity}
                              >
                                  <option value="">-- Select Warehouse --</option>
                                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                              </select>
                          </div>

                          <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bin Location</label>
                              <select 
                                  name="binName" 
                                  value={initialStock.binName} 
                                  onChange={handleStockChange} 
                                  className="w-full border border-blue-200 px-4 py-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                  disabled={!initialStock.warehouseId}
                              >
                                  <option value="">-- Select Bin --</option>
                                  {warehouses.find(w => String(w.id) === String(initialStock.warehouseId))?.bins.map(bin => (
                                      <option key={bin} value={bin}>{bin}</option>
                                  ))}
                              </select>
                          </div>
                      </div>

                      {initialStock.quantity > 0 && (
                          <div className="mt-4 text-right text-sm text-blue-700 font-medium">
                              Estimated Value: <Price amount={totalValue} />
                          </div>
                      )}
                  </div>
              )}

              <div className="pt-4 flex gap-4 justify-end">
                  <button 
                      type="button" 
                      onClick={() => navigate('/admin/inventory')}
                      className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
                  >
                      Cancel
                  </button>
                  <button 
                      type="submit" 
                      className="px-8 py-2.5 rounded-lg bg-gray-900 text-white font-medium hover:bg-gray-800 shadow-lg hover:shadow-xl transition transform hover:-translate-y-0.5"
                  >
                      {isEditing ? "Update Product" : "Save & Create"}
                  </button>
              </div>

          </form>
        </div>
      </div>

      {useUnsavedChanges(isDirty)}
      <NavigationBlocker isDirty={isDirty} />
    </>
  );
};

export default InventoryForm;